<script lang="ts">
  // Choropleth of a race's units (precincts or counties). Modes: Margin · Shift vs baseline 1/2 · Reporting ·
  // Turnout. Shapes + details UX (hover popover / tap bottom sheet / keyboard) live in MapCanvas.
  // Shift modes are NYT-style arrows: from each fully counted place, an arrow toward the side it moved (left = the
  // first side: Democrats / Yes; right = Republicans / No), as long as the shift in margin since the baseline election.
  // Fully counted places (solid arrows) are compared with the whole baseline result. Partly counted places (faint
  // arrows) are compared like for like: the baseline's margin over the same kinds of votes counted so far, weighted
  // the way today's count is (early with early, Election Day with Election Day) — so the map shows how the night is
  // looking without the early-votes-first bias.
  import type { RaceFile, Unit } from '../../types';
  import MapCanvas, { type MapArrow, type MapItem } from './MapCanvas.svelte';
  import type { GeoCollection, GeoFeature } from '../../lib/store';
  import { featureFor, featuresById, isPseudo, MARGIN_BINS, mapMix, TURNOUT_BINS, type Bin, type MapMode } from '../../lib/geo';
  import { fmtInt, fmtPct, fmtRatioPct } from '../../lib/format';
  import { baselineLabel, cardName, colorVar, marginShort, marginText, partyTag, rankIn, resolvePair, shortContest } from '../../lib/race';

  let { race, geo, mode = $bindable('margin'), maxHeight = 620 }: { race: RaceFile; geo: GeoCollection; mode?: MapMode; maxHeight?: number } = $props();

  const pair = $derived(resolvePair(race));
  // vote-for-N: `pair` is the last-seat cutoff, not a lead — colour each place by its own leader instead
  const multi = $derived(race.vote_for > 1);
  const leaderIn = (u: Unit) => rankIn(race.choices, u)[0] ?? null;
  const leadIn = (u: Unit) => {
    const [a, b] = rankIn(race.choices, u);
    return a && u.total > 0 ? (((u.votes[a.choice_id] ?? 0) - (b ? u.votes[b.choice_id] ?? 0 : 0)) / u.total) * 100 : 0;
  };
  const base1 = $derived(shortContest(race.baselines[0]?.contest, baselineLabel(race.baselines, 1, 'last time')));
  const base2 = $derived(shortContest(race.baselines[1]?.contest, baselineLabel(race.baselines, 2, 'before')));
  const shiftMode = $derived(mode === 'swing_b22' || mode === 'swing_b24');
  const baseName = $derived(mode === 'swing_b24' ? base2 : base1);
  /** the baseline a place is compared with: the whole result once fully counted, else the same kinds of votes */
  const baseOf = (u: Unit): { v: number; partial: boolean } | null => {
    if (u.reported === 'complete') {
      const b = mode === 'swing_b24' ? u.margin_b24 : u.margin_b22;
      return b === null ? null : { v: b, partial: false };
    }
    const b = mode === 'swing_b24' ? u.margin_b24_mix : u.margin_b22_mix;
    return b === null || b === undefined ? null : { v: b, partial: true };
  };
  /** shift in margin (pair orientation, points), or null (no votes / no comparable baseline) */
  const shiftOf = (u: Unit): { v: number; partial: boolean } | null => {
    if (!u.total || u.margin === null || isPseudo(u.unit_id)) return null;
    const b = baseOf(u);
    return b ? { v: u.margin - b.v, partial: b.partial } : null;
  };
  const arrowFor = (id: string): MapArrow | null => {
    const u = byUnit.get(id);
    const s = u ? shiftOf(u) : null;
    return s === null || !pair ? null : { shift: s.v, color: colorVar(s.v >= 0 ? pair[0] : pair[1]), faint: s.partial };
  };
  /** vote-weighted shift over fully counted places, and separately over partly counted ones (like for like) */
  const shiftSummary = $derived.by(() => {
    if (!shiftMode || !pair) return null;
    const acc = { full: { w: 0, now: 0, then: 0, n: 0 }, part: { w: 0, now: 0, then: 0, n: 0 } };
    for (const u of race.units) {
      const s = shiftOf(u);
      if (!s || u.margin === null) continue;
      const a = s.partial ? acc.part : acc.full;
      a.w += u.total; a.now += u.margin * u.total; a.then += (u.margin - s.v) * u.total; a.n++;
    }
    const fin = (a: typeof acc.full) => (a.n ? { n: a.n, shift: (a.now - a.then) / a.w, now: a.now / a.w, then: a.then / a.w } : null);
    return { full: fin(acc.full), part: fin(acc.part) };
  });
  const modes = $derived.by(() => {
    const out: { value: MapMode; label: string }[] = [];
    if (pair) out.push({ value: 'margin', label: race.kind === 'referendum' ? 'Yes / No' : multi ? 'Leader' : 'Margin' });
    // short button labels (the year) when the two baselines are different years; full names in the legend
    const y1 = base1.slice(0, 4), y2 = base2.slice(0, 4);
    const short = /^\d{4}$/.test(y1) && /^\d{4}$/.test(y2) && y1 !== y2;
    if (pair && !multi && race.units.some((u) => u.margin_b22 !== null || (u.margin_b22_mix ?? null) !== null)) out.push({ value: 'swing_b22', label: `Shift vs ${short ? y1 : base1}` });
    if (pair && !multi && race.units.some((u) => u.margin_b24 !== null || (u.margin_b24_mix ?? null) !== null)) out.push({ value: 'swing_b24', label: `Shift vs ${short ? y2 : base2}` });
    out.push({ value: 'reporting', label: 'Reporting' });
    if (race.units.some((u) => u.turnout_vs_2022 !== null)) out.push({ value: 'turnout', label: 'Turnout' });
    return out;
  });
  $effect(() => { if (!modes.some((m) => m.value === mode)) mode = modes[0]?.value ?? 'reporting'; });

  // geometry depends only on the set of unit ids (a string, so a new snapshot with the same units reuses it)
  const unitIds = $derived(race.units.map((u) => u.unit_id).join('\n'));
  const joined = $derived.by(() => {
    const byId = featuresById(geo);
    return unitIds.split('\n').map((id) => [id, featureFor(byId, id)] as const).filter((x): x is readonly [string, GeoFeature] => !!x[1]);
  });
  const byUnit = $derived(new Map(race.units.map((u) => [u.unit_id, u])));
  const missing = $derived(race.units.filter((u) => !isPseudo(u.unit_id)).length - joined.length);
  // stable identity while the unit set is unchanged → MapCanvas re-projects only when shapes actually change
  const items = $derived<MapItem[]>(joined.map(([id, f]) => ({ id, f })));
  const nameOf = (id: string) => byUnit.get(id)?.name ?? id;

  function binOf(v: number, bins: Bin[]) { return bins.find((b) => Math.abs(v) < b.max) ?? bins[bins.length - 1]!; }
  function fill(u: Unit): string {
    const div = (v: number | null, bins: Bin[]) => (v === null || !pair ? 'var(--map-empty)' : mapMix(colorVar(v >= 0 ? pair[0] : pair[1]), binOf(v, bins).mix));
    switch (mode) {
      case 'margin':
        if (multi) { const l = leaderIn(u); return u.total > 0 && l ? mapMix(colorVar(l), binOf(leadIn(u), MARGIN_BINS).mix) : 'var(--map-empty)'; }
        return u.total > 0 ? div(u.margin, MARGIN_BINS) : 'var(--map-empty)';
      case 'swing_b22':
      case 'swing_b24':      // neutral base under the arrows; fully counted places a shade darker
        return u.reported === 'complete' ? 'var(--map-shift-done)' : u.total > 0 ? 'var(--map-shift-part)' : 'var(--map-empty)';
      case 'reporting': return u.reported === 'complete' ? mapMix('var(--ink-2)', 85) : u.reported === 'partial' ? mapMix('var(--ink-3)', 40) : 'var(--map-empty)';
      case 'turnout': {
        const v = u.turnout_vs_2022;
        if (v === null) return 'var(--map-empty)';
        return mapMix('var(--c-turnout)', (TURNOUT_BINS.find((b) => v < b.max) ?? TURNOUT_BINS[TURNOUT_BINS.length - 1]!).mix);
      }
    }
  }
  const legend = $derived.by((): { c: string; label: string }[] => {
    if (mode === 'margin' && multi) {
      return race.choices.filter((c) => c.votes > 0).slice(0, 8).map((c) => ({ c: colorVar(c), label: cardName(c, race.kind) }));
    }
    if (mode === 'margin' && pair) {
      const bins = MARGIN_BINS;
      const tag = (i: 0 | 1) => marginShort(i === 0 ? 1 : -1, pair, race.kind, 0).replace(/\d+$/, '');
      return [...[...bins].reverse().map((b) => ({ c: mapMix(colorVar(pair[0]), b.mix), label: `${tag(0)}${b.label}` })), ...bins.map((b) => ({ c: mapMix(colorVar(pair[1]), b.mix), label: `${tag(1)}${b.label}` }))];
    }
    if (mode === 'reporting') return [{ c: mapMix('var(--ink-2)', 85), label: 'All in' }, { c: mapMix('var(--ink-3)', 40), label: 'Partly' }, { c: 'var(--map-empty)', label: 'None yet' }];
    return TURNOUT_BINS.map((b) => ({ c: mapMix('var(--c-turnout)', b.mix), label: b.label }));
  });
  const legendTitle = $derived(mode === 'margin' ? (multi ? 'Leader in votes counted (darker = bigger lead)' : 'Lead in votes counted') : mode === 'reporting' ? 'Precincts reporting' : mode === 'turnout' ? 'Ballots so far vs all of 2022' : 'Shift in margin toward');
  const unitWord = $derived(race.unit_kind === 'county' ? 'counties' : 'precincts');
  const sideName = (i: 0 | 1) => (pair ? marginShort(i === 0 ? 1 : -1, pair, race.kind, 0).replace(/\s*\+\d+$/, '') : '');

  const ariaFor = (id: string) => {
    const u = byUnit.get(id);
    if (!u) return id;
    if (multi) { const l = leaderIn(u); return `${u.name}: ${u.total > 0 && l ? `${cardName(l, race.kind)} leads with ${fmtPct(((u.votes[l.choice_id] ?? 0) / u.total) * 100)}` : 'no votes yet'}`; }
    return `${u.name}: ${u.margin !== null && pair ? marginText(u.margin, pair, race.kind) : 'no votes yet'}`;
  };
  const fillId = (id: string) => { const u = byUnit.get(id); return u ? fill(u) : 'var(--map-empty)'; };
  const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;
  const top3 = (u: Unit) => rankIn(race.choices, u).slice(0, 3);
</script>

<div class="map">
  <div class="seg" role="group" aria-label="Map shows">
    {#each modes as o (o.value)}<button type="button" aria-pressed={mode === o.value} onclick={() => (mode = o.value)}>{o.label}</button>{/each}
  </div>
  {#if joined.length === 0}
    <div class="nomap">No map shapes match this race.</div>
  {:else}
    <MapCanvas {items} {nameOf} fill={fillId} {ariaFor} label="Map of {race.title} by {race.unit_kind}" {maxHeight} arrow={shiftMode ? arrowFor : null}>
      {#snippet popup(id)}
        {@const u = byUnit.get(id)}
        {#if u}
        <div class="pt">{u.name}{race.unit_kind === 'county' ? ' County' : ''}</div>
        <div class="ps">{u.reported === 'complete' ? 'All votes in' : u.reported === 'partial' ? 'Partly counted' : 'No votes yet'} · {fmtInt(u.total)} votes</div>
        {#if u.total > 0}
          {#each top3(u) as c (c.choice_id)}
            <div class="pr"><i style="background:{colorVar(c)}"></i><span>{cardName(c, race.kind)}{partyTag(c)}</span><b class="num">{fmtPct(((u.votes[c.choice_id] ?? 0) / u.total) * 100)}</b></div>
          {/each}
        {/if}
        {#if pair && shiftMode}
          {@const s = shiftOf(u)}
          {#if s && u.margin !== null}
            <div class="pm">{baseName}{s.partial ? ' (same kinds of votes)' : ''}: <b>{marginShort(u.margin - s.v, pair, race.kind)}</b> → now <b>{marginShort(u.margin, pair, race.kind)}</b></div>
            <div class="pm">Shift: <b style="color:{colorVar(s.v >= 0 ? pair[0] : pair[1])}">{marginShort(s.v, pair, race.kind)}</b>{s.partial ? ' · partly counted' : ''}</div>
          {:else}
            <div class="pm">{u.total > 0 ? `No comparable ${baseName} result` : 'No votes yet'}</div>
          {/if}
        {:else if pair && u.swing_b22 !== null}<div class="pm">Shift vs {base1}: <b>{marginShort(u.swing_b22, pair, race.kind)}</b></div>{/if}
        {#if u.turnout_vs_2022 !== null}<div class="pm">Ballots vs 2022: <b>{fmtRatioPct(u.turnout_vs_2022)}</b></div>{/if}
        {/if}
      {/snippet}
    </MapCanvas>
  {/if}
  {#if shiftMode && pair}
    <div class="legend">
      <span class="lt">Shift in margin vs the {baseName} race</span>
      <span class="li"><svg width="30" height="16" aria-hidden="true"><path d="M26,13L6,4M10.5,3.2L6,4L8.5,7.8" style="stroke:{colorVar(pair[0])}" /></svg>More {sideName(0)}</span>
      <span class="li">More {sideName(1)}<svg width="30" height="16" aria-hidden="true"><path d="M4,13L24,4M19.5,3.2L24,4L21.5,7.8" style="stroke:{colorVar(pair[1])}" /></svg></span>
      <span class="li sizes">Longer arrow = bigger shift (full length at 25+ points) · faint = partly counted</span>
    </div>
    <div class="shiftsum">
      {#if shiftSummary?.full}
        {@const f = shiftSummary.full}
        <p>The {f.n} fully counted {unitWord} {f.n === 1 ? 'has' : 'have'} moved
        <b style="color:{colorVar(f.shift >= 0 ? pair[0] : pair[1])}">{Math.abs(f.shift).toFixed(1)} points toward {sideName(f.shift >= 0 ? 0 : 1)}</b>
        compared with the {baseName} race ({marginShort(f.then, pair, race.kind)} then, {marginShort(f.now, pair, race.kind)} now).</p>
      {/if}
      {#if shiftSummary?.part}
        {@const q = shiftSummary.part}
        <p class="part">{shiftSummary.full ? 'In' : 'So far, in'} the {q.n} partly counted {unitWord}, comparing the same kinds of votes (early with early, Election Day with Election Day):
        <b style="color:{colorVar(q.shift >= 0 ? pair[0] : pair[1])}">{Math.abs(q.shift).toFixed(1)} points toward {sideName(q.shift >= 0 ? 0 : 1)}</b>.</p>
      {/if}
      {#if !shiftSummary?.full && !shiftSummary?.part}Arrows appear as {unitWord} report.{/if}
    </div>
  {:else}
    <div class="legend">
      <span class="lt">{legendTitle}</span>
      {#each legend as l}<span class="li"><i style="background:{l.c}"></i>{l.label}</span>{/each}
    </div>
  {/if}
  <div class="note">
    {#if geo.placeholder}Placeholder shapes (test data). {/if}{#if missing > 0}{race.unit_kind === 'county' ? plural(missing, 'county', 'counties') : plural(missing, 'precinct', 'precincts')} without a shape. {/if}Tap or hover a {race.unit_kind === 'county' ? 'county' : 'precinct'} for details.
  </div>
</div>

<style>
  .map { display: flex; flex-direction: column; gap: 12px; }
  .nomap { color: var(--ink-3); padding: 30px; text-align: center; }
  .pt { font: 700 1rem/1.2 var(--display); }
  .ps { color: var(--ink-3); font-size: 0.78rem; margin: 2px 0 8px; }
  .pr { display: flex; align-items: center; gap: 8px; padding: 2px 0; }
  .pr i { width: 9px; height: 9px; border-radius: 3px; flex: none; }
  .pr span { flex: 1; min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  .pm { margin-top: 6px; color: var(--ink-2); font-size: 0.8rem; }
  .legend { display: flex; flex-wrap: wrap; gap: 6px 10px; align-items: center; font-size: 0.76rem; color: var(--ink-2); }
  .lt { font-weight: 700; color: var(--ink-3); margin-right: 4px; }
  .li { display: inline-flex; align-items: center; gap: 5px; }
  .li i { width: 14px; height: 10px; border-radius: 3px; }
  .note { font-size: 0.76rem; color: var(--ink-3); }
  .li svg { flex: none; overflow: visible; }
  .li svg path { fill: none; stroke-width: 2; stroke-linecap: round; stroke-linejoin: round; }
  .shiftsum { font-size: 0.86rem; color: var(--ink-2); line-height: 1.45; }
  .shiftsum p { margin: 0 0 4px; }
  .shiftsum .part { color: var(--ink-3); }
</style>
